// Convention `notes_libres` v1 (D6, tech-design §3.2) — module 100 % pur.
// `notes_libres` (champ CommandePizza) = texte libre + AU PLUS UNE ligne machine
// finale, préfixée et versionnée :
//   #TB:v1:{"mod":["-champignons","+parmesan"],"flag":["refaite"]}
//
// - JSON compact (échappement natif JSON.stringify), préfixe exact `#TB:v1:`.
// - Anti-collision : toute ligne HUMAINE commençant par `#TB:` est préfixée d'un
//   espace à la composition (et dé-préfixée au parse) — elle ne peut donc jamais
//   être confondue avec le bloc machine.
// - Parse = DERNIÈRE ligne seulement ; bloc reconnu ssi préfixe exact + JSON.parse
//   réussi sur un objet. Sinon dégradation sûre : tout devient texte libre.

const PREFIX = '#TB:v1:';
// Détecte une ligne (humaine ou machine) commençant par le sentinel `#TB:`.
const SENTINEL = /^#TB:/;
// Détecte une ligne humaine échappée (espace + sentinel) à ré-inverser au parse.
const ESCAPED_SENTINEL = /^ #TB:/;

export interface ParsedNotes {
  /** Texte libre (peut être vide). */
  texte: string;
  /** Modifications D6 (ex. ["-champignons", "+parmesan"]). */
  mod: string[];
  /** Drapeaux D6 (ex. ["refaite"]). */
  flag: string[];
}

export interface ComposeMeta {
  mod?: readonly string[];
  flag?: readonly string[];
}

function escapeHuman(texte: string): string {
  return texte
    .split('\n')
    .map((line) => (SENTINEL.test(line) ? ' ' + line : line))
    .join('\n');
}

function unescapeHuman(lines: readonly string[]): string {
  return lines
    .map((line) => (ESCAPED_SENTINEL.test(line) ? line.slice(1) : line))
    .join('\n');
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((x): x is string => typeof x === 'string');
}

// Compose `notes_libres` : texte libre (échappé anti-collision) + bloc machine
// final SI et seulement si `mod` ou `flag` est non vide.
export function composeNotes(texte: string, meta: ComposeMeta = {}): string {
  const mod = [...(meta.mod ?? [])];
  const flag = [...(meta.flag ?? [])];
  const escaped = escapeHuman(texte);
  const hasBlock = mod.length > 0 || flag.length > 0;
  if (!hasBlock) return escaped;
  const block = PREFIX + JSON.stringify({ mod, flag });
  return escaped.length > 0 ? `${escaped}\n${block}` : block;
}

// Parse `notes_libres` : n'inspecte QUE la dernière ligne. Bloc valide → sépare
// texte / mod / flag. Sinon (pas de bloc, préfixe absent, JSON invalide) →
// dégradation sûre : tout = texte libre, mod/flag vides.
export function parseNotes(notes: string): ParsedNotes {
  const lines = notes.split('\n');
  const last = lines[lines.length - 1] ?? '';
  if (last.startsWith(PREFIX)) {
    try {
      const parsed: unknown = JSON.parse(last.slice(PREFIX.length));
      if (parsed !== null && typeof parsed === 'object') {
        const record = parsed as Record<string, unknown>;
        return {
          texte: unescapeHuman(lines.slice(0, -1)),
          mod: toStringArray(record.mod),
          flag: toStringArray(record.flag),
        };
      }
    } catch {
      // JSON invalide → dégradation sûre (tout = texte libre).
    }
  }
  return { texte: unescapeHuman(lines), mod: [], flag: [] };
}
